import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import timezoneService from '../services/timezoneService';
import { aiTrackingService } from '../services/aiTrackingService';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Slider,
  Button,
  Divider,
  Alert,
  Chip,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  SmartToy,
  CameraAlt,
  Schedule,
  Analytics,
  Settings as SettingsIcon,
  TrendingUp,
  PhotoCamera,
  Token,
  AttachMoney,
  Timeline,
  Assessment,
  FolderOpen,
  Home,
  Computer,
  Close,
  ArrowBack,
  Folder,
  ChevronRight,
  Key,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Warning,
  ErrorOutline,
} from '@mui/icons-material';

const API_BASE = process.env.NODE_ENV === 'production'
  ? window.location.origin
  : `http://${window.location.hostname}:3002`;

interface AISettingsProps {}

interface AIConfig {
  enabled: boolean;
  screenshotInterval: number; // in minutes
  autoDetection: boolean;
  projectMatching: boolean;
  confidenceThreshold: number;
  saveScreenshots: 'none' | 'server' | 'local';
  localSavePath: string;
  analysisDepth: 'basic' | 'detailed' | 'comprehensive';
}

const AISettings: React.FC<AISettingsProps> = () => {
  const [config, setConfig] = useState<AIConfig>({
    enabled: false,
    screenshotInterval: 5,
    autoDetection: true,
    projectMatching: true,
    confidenceThreshold: 0.7,
    saveScreenshots: 'server',
    localSavePath: '',
    analysisDepth: 'detailed',
  });

  const [isRunning, setIsRunning] = useState(false);
  const [lastScreenshot, setLastScreenshot] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // AI Key / Trial state
  const [aiKeyStatus, setAiKeyStatus] = useState<any>(null);
  const [byokKey, setByokKey] = useState('');
  const [showByokKey, setShowByokKey] = useState(false);
  const [savingApiKey, setSavingApiKey] = useState(false);
  const [apiKeyError, setApiKeyError] = useState('');
  const [apiKeySuccess, setApiKeySuccess] = useState('');
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [tempPath, setTempPath] = useState('');
  const [currentPath, setCurrentPath] = useState('/Users');
  const [folders, setFolders] = useState<string[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  // Load folders using File System Access API or fallback
  const loadFolders = async (path: string) => {
    setLoadingFolders(true);
    try {
      console.log('Loading folders for path:', path);
      
      // Check if File System Access API is available
      if ('showDirectoryPicker' in window) {
        console.log('File System Access API available - using browser native folder access');
        // For File System Access API, we don't need to load folders from server
        // The user will pick folders directly through browser dialog
        setFolders(['Click "Choose Folder" to select from your computer']);
      } else {
        console.log('File System Access API not available - trying server API');
        try {
          const folders = await apiService.getFolders(path);
          console.log('Found folders:', folders);
          setFolders(folders);
        } catch (error) {
          console.error('Server API failed:', error);
          setFolders(['Server folder access not available']);
        }
      }
    } catch (error) {
      console.error('Failed to load folders:', error);
      setFolders(['Unable to access folders']);
    } finally {
      setLoadingFolders(false);
    }
  };

  const navigateToFolder = (folderName: string) => {
    const newPath = currentPath === '/' ? `/${folderName}` : `${currentPath}/${folderName}`;
    setCurrentPath(newPath);
    setTempPath(newPath);
    loadFolders(newPath);
  };

  const navigateUp = () => {
    const pathParts = currentPath.split('/').filter(part => part !== '');
    if (pathParts.length > 1) {
      const newPath = '/' + pathParts.slice(0, -1).join('/');
      setCurrentPath(newPath);
      setTempPath(newPath);
      loadFolders(newPath);
    } else if (pathParts.length === 1) {
      setCurrentPath('/Users');
      setTempPath('/Users');
      loadFolders('/Users');
    }
  };

  // Subscribe to AI tracking service
  useEffect(() => {
    const unsubscribe = aiTrackingService.subscribe((status) => {
      setIsRunning(status.isRunning);
      setLastScreenshot(status.lastScreenshot);
    });

    return unsubscribe;
  }, []);

  // Load AI settings and key status on mount
  useEffect(() => {
    loadAISettings();
    loadAiKeyStatus();
  }, []);

  const loadAiKeyStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/ai-key/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setAiKeyStatus(data.data);
    } catch (e) {
      console.error('Failed to load AI key status', e);
    }
  };

  const saveApiKey = async (keyToSave: string | null) => {
    setSavingApiKey(true);
    setApiKeyError('');
    setApiKeySuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/ai-key/byok`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ apiKey: keyToSave }),
      });
      const data = await res.json();
      if (data.success) {
        setApiKeySuccess(data.message);
        setByokKey('');
        await loadAiKeyStatus();
      } else {
        setApiKeyError(data.error || 'Failed to save API key');
      }
    } catch (e) {
      setApiKeyError('Network error. Please try again.');
    } finally {
      setSavingApiKey(false);
    }
  };

  // Load folders when dialog opens
  useEffect(() => {
    if (folderDialogOpen) {
      const initialPath = config.localSavePath || '/Users/admin/Documents';
      setCurrentPath(initialPath);
      setTempPath(initialPath);
      loadFolders(initialPath);
    }
  }, [folderDialogOpen]);

  const loadAISettings = async () => {
    try {
      setLoading(true);
      const settings = await apiService.getAISettings();
      
      // Map backend settings to frontend config
      let saveScreenshotsValue: 'none' | 'server' | 'local' = 'server';
      if (settings.saveScreenshots === false || settings.saveScreenshots === 'none') {
        saveScreenshotsValue = 'none';
      } else if (settings.saveScreenshots === 'local') {
        saveScreenshotsValue = 'local';
      } else {
        saveScreenshotsValue = 'server';
      }

      setConfig({
        enabled: settings.screenshotsEnabled || false,
        screenshotInterval: settings.screenshotInterval || 5,
        autoDetection: settings.autoProjectDetection || true,
        projectMatching: settings.smartProjectMatching || true,
        confidenceThreshold: (settings.confidenceThreshold || 70) / 100,
        saveScreenshots: saveScreenshotsValue,
        localSavePath: settings.localSavePath || '',
        analysisDepth: settings.analysisDepth || 'detailed',
      });
      
      setIsRunning(settings.aiTrackingEnabled || false);
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAISettings = async () => {
    try {
      setSaving(true);
      
      // Map frontend config to backend settings
      const settings = {
        aiTrackingEnabled: isRunning,
        screenshotsEnabled: config.enabled,
        screenshotInterval: config.screenshotInterval,
        saveScreenshots: config.saveScreenshots,
        localSavePath: config.localSavePath,
        autoProjectDetection: config.autoDetection,
        smartProjectMatching: config.projectMatching,
        analysisDepth: config.analysisDepth,
        confidenceThreshold: Math.round(config.confidenceThreshold * 100),
      };
      
      await apiService.updateAISettings(settings);
      
      // Show success message or handle success
      console.log('AI settings saved successfully');
    } catch (error) {
      console.error('Failed to save AI settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (key: keyof AIConfig, value: any) => {
    const newConfig = {
      ...config,
      [key]: value
    };
    setConfig(newConfig);
    
    // Update the global AI tracking service with new config
    aiTrackingService.updateConfig(newConfig);
  };

  const handleStartStop = async () => {
    if (isRunning) {
      // Stop AI tracking using global service
      aiTrackingService.stop();
      setIsRunning(false);
    } else {
      // Ensure screenshots are enabled before starting
      const startConfig = {
        ...config,
        enabled: true
      };
      
      // Update local config
      setConfig(startConfig);
      
      // Save settings first
      const settings = {
        aiTrackingEnabled: true,
        screenshotsEnabled: true,
        screenshotInterval: startConfig.screenshotInterval,
        saveScreenshots: startConfig.saveScreenshots,
        autoProjectDetection: startConfig.autoDetection,
        smartProjectMatching: startConfig.projectMatching,
        analysisDepth: startConfig.analysisDepth,
        confidenceThreshold: Math.round(startConfig.confidenceThreshold * 100),
      };
      
      try {
        await apiService.updateAISettings(settings);
        
        // Start AI tracking using global service
        await aiTrackingService.start(startConfig);
        setIsRunning(true);
        
        console.log('AI tracking started successfully');
      } catch (error) {
        console.error('Failed to start AI tracking:', error);
      }
    }
  };

  const intervalOptions = [
    { value: 1, label: '1 minute' },
    { value: 5, label: '5 minutes' },
    { value: 10, label: '10 minutes' },
    { value: 30, label: '30 minutes' },
  ];

  const styles = {
    container: {
      backgroundColor: 'white',
      minHeight: '100vh',
      padding: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '24px'
    },
    card: {
      marginBottom: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      borderRadius: '12px'
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    settingRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px'
    },
    settingLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333'
    },
    settingDescription: {
      fontSize: '12px',
      color: '#666',
      marginTop: '4px'
    },
    statusCard: {
      backgroundColor: isRunning ? '#e8f5e8' : '#f5f5f5',
      border: `1px solid ${isRunning ? '#4caf50' : '#ddd'}`
    },
    statusIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px'
    },
    statusDot: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      backgroundColor: isRunning ? '#4caf50' : '#999'
    },
    controlButton: {
      backgroundColor: isRunning ? '#f44336' : '#4caf50',
      color: 'white',
      '&:hover': {
        backgroundColor: isRunning ? '#d32f2f' : '#45a049'
      }
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <SmartToy sx={{ fontSize: '32px', color: '#e67e22' }} />
        <h1 style={styles.title}>AI Screenshot Analysis</h1>
      </div>
      
      <p style={styles.subtitle}>
        Configure how AI automatically captures and analyzes your screen to track time and projects.
      </p>

      {/* Status Card */}
      <Card sx={styles.statusCard}>
        <CardContent>
          <div style={styles.statusIndicator}>
            <div style={styles.statusDot}></div>
            <Typography variant="h6" sx={{ fontWeight: '600' }}>
              AI Tracking: {isRunning ? 'Active' : 'Inactive'}
            </Typography>
          </div>
          
          {isRunning && lastScreenshot && (
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Last screenshot: {timezoneService.formatUserTime(lastScreenshot, { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
          
          <Button
            variant="contained"
            onClick={handleStartStop}
            sx={styles.controlButton}
            startIcon={isRunning ? <SettingsIcon /> : <CameraAlt />}
          >
            {isRunning ? 'Stop AI Tracking' : 'Start AI Tracking'}
          </Button>
        </CardContent>
      </Card>

      {/* Screenshot Settings */}
      <Card sx={styles.card}>
        <CardContent>
          <Typography sx={styles.cardTitle}>
            <CameraAlt />
            Screenshot Settings
          </Typography>
          
          <div style={styles.settingRow}>
            <div>
              <div style={styles.settingLabel}>Enable Screenshots</div>
              <div style={styles.settingDescription}>
                Allow AI to take automatic screenshots for analysis
              </div>
            </div>
            <Switch
              checked={config.enabled}
              onChange={(e) => handleConfigChange('enabled', e.target.checked)}
              color="primary"
            />
          </div>

          <Divider sx={{ my: 2 }} />

          <div style={styles.settingRow}>
            <div>
              <div style={styles.settingLabel}>Screenshot Interval</div>
              <div style={styles.settingDescription}>
                How often to capture screenshots
              </div>
            </div>
            <FormControl>
              <RadioGroup
                value={config.screenshotInterval}
                onChange={(e) => handleConfigChange('screenshotInterval', parseInt(e.target.value))}
                row
              >
                {intervalOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" />}
                    label={option.label}
                    sx={{ mr: 2 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </div>

          <Divider sx={{ my: 2 }} />

          <div>
            <div style={styles.settingLabel}>Save Screenshots</div>
            <div style={styles.settingDescription}>
              Choose where to store screenshots for review and thumbnails
            </div>
            <FormControl sx={{ mt: 2 }}>
              <RadioGroup
                value={config.saveScreenshots}
                onChange={(e) => handleConfigChange('saveScreenshots', e.target.value)}
              >
                <FormControlLabel
                  value="none"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">Don't Save</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Only analyze screenshots, don't store them
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="server"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">Save to Server</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Store screenshots on the server for team access
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="local"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">Save Locally</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Store screenshots on your local device only
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
            
            {/* Local Save Path Selector - only show when "local" is selected */}
            {config.saveScreenshots === 'local' && (
              <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" fontWeight="500" sx={{ mb: 2 }}>
                  Local Save Location
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ 
                      p: 1.5, 
                      border: '1px solid #ddd', 
                      borderRadius: 1, 
                      backgroundColor: 'white',
                      fontFamily: 'monospace',
                      fontSize: '13px'
                    }}>
                      {config.localSavePath || 'No folder selected'}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FolderOpen />}
                    onClick={async () => {
                      // Check if File System Access API is available
                      if ('showDirectoryPicker' in window) {
                        try {
                          console.log('🗂️ Using File System Access API to pick folder');
                          // @ts-ignore - File System Access API is not in TypeScript types yet
                          const dirHandle = await window.showDirectoryPicker({
                            mode: 'readwrite',
                            startIn: 'documents'
                          });
                          const folderPath = dirHandle.name;
                          console.log('📁 Selected folder:', folderPath);
                          
                          // Set the selected folder path
                          handleConfigChange('localSavePath', folderPath);
                          
                          // Store the directory handle in the AI tracking service
                          // This will be used when actually saving screenshots
                          aiTrackingService.setDirectoryHandle(dirHandle);
                          
                          // Show success message
                          console.log('✅ Folder selected successfully:', folderPath);
                          
                          // Show user notification
                          const notification = document.createElement('div');
                          notification.style.cssText = `
                            position: fixed;
                            top: 20px;
                            right: 20px;
                            background: #4caf50;
                            color: white;
                            padding: 12px 20px;
                            border-radius: 8px;
                            box-shadow: 0 4px 12px rgba(0,0,0,0.2);
                            z-index: 10000;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                            font-size: 14px;
                            max-width: 350px;
                          `;
                          notification.innerHTML = `✅ Folder selected: ${folderPath}`;
                          
                          document.body.appendChild(notification);
                          
                          setTimeout(() => {
                            if (notification.parentNode) {
                              notification.parentNode.removeChild(notification);
                            }
                          }, 3000);
                          
                        } catch (error: any) {
                          console.log('❌ User cancelled folder selection or error:', error);
                          if (error.name !== 'AbortError') {
                            // Show error notification for non-cancellation errors
                            const notification = document.createElement('div');
                            notification.style.cssText = `
                              position: fixed;
                              top: 20px;
                              right: 20px;
                              background: #f44336;
                              color: white;
                              padding: 12px 20px;
                              border-radius: 8px;
                              box-shadow: 0 4px 12px rgba(0,0,0,0.2);
                              z-index: 10000;
                              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                              font-size: 14px;
                              max-width: 350px;
                            `;
                            notification.innerHTML = `❌ Failed to select folder: ${error.message}`;
                            
                            document.body.appendChild(notification);
                            
                            setTimeout(() => {
                              if (notification.parentNode) {
                                notification.parentNode.removeChild(notification);
                              }
                            }, 4000);
                          }
                        }
                      } else {
                        // Fallback to dialog-based folder selection
                        console.log('🗂️ File System Access API not available, using dialog');
                        setTempPath(config.localSavePath);
                        setFolderDialogOpen(true);
                      }
                    }}
                    sx={{ minWidth: '120px' }}
                  >
                    Choose Folder
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Screenshots will be saved to this folder on your computer. Make sure the folder exists and you have write permissions.
                </Typography>
              </Box>
            )}
          </div>
        </CardContent>
      </Card>

      {/* AI Analysis Settings */}
      <Card sx={styles.card}>
        <CardContent>
          <Typography sx={styles.cardTitle}>
            <Analytics />
            AI Analysis Settings
          </Typography>
          
          <div style={styles.settingRow}>
            <div>
              <div style={styles.settingLabel}>Auto Project Detection</div>
              <div style={styles.settingDescription}>
                Automatically detect and match projects based on screen content
              </div>
            </div>
            <Switch
              checked={config.autoDetection}
              onChange={(e) => handleConfigChange('autoDetection', e.target.checked)}
              color="primary"
            />
          </div>

          <Divider sx={{ my: 2 }} />

          <div style={styles.settingRow}>
            <div>
              <div style={styles.settingLabel}>Smart Project Matching</div>
              <div style={styles.settingDescription}>
                Group similar activities into the same time entry
              </div>
            </div>
            <Switch
              checked={config.projectMatching}
              onChange={(e) => handleConfigChange('projectMatching', e.target.checked)}
              color="primary"
            />
          </div>

          <Divider sx={{ my: 2 }} />

          <div>
            <div style={styles.settingLabel}>Analysis Depth</div>
            <div style={styles.settingDescription}>
              How detailed should the AI analysis be
            </div>
            <FormControl sx={{ mt: 2 }}>
              <RadioGroup
                value={config.analysisDepth}
                onChange={(e) => handleConfigChange('analysisDepth', e.target.value)}
              >
                <FormControlLabel
                  value="basic"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">Basic</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Simple activity detection
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="detailed"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">Detailed</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Project and task identification
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="comprehensive"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">Comprehensive</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Full context analysis with productivity insights
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </div>

          <Divider sx={{ my: 2 }} />

          <div>
            <div style={styles.settingLabel}>Confidence Threshold</div>
            <div style={styles.settingDescription}>
              Minimum confidence level for automatic project matching
            </div>
            <Box sx={{ mt: 2, px: 2 }}>
              <Slider
                value={config.confidenceThreshold * 100}
                onChange={(_, value) => handleConfigChange('confidenceThreshold', (value as number) / 100)}
                min={50}
                max={95}
                step={5}
                marks={[
                  { value: 50, label: '50%' },
                  { value: 70, label: '70%' },
                  { value: 90, label: '90%' }
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}%`}
              />
            </Box>
          </div>
        </CardContent>
      </Card>


      {/* Privacy & Security */}
      <Card sx={styles.card}>
        <CardContent>
          <Typography sx={styles.cardTitle}>
            <SettingsIcon />
            Privacy & Security
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            All screenshots are processed locally and encrypted. No sensitive data is sent to external servers.
          </Alert>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label="Local Processing" color="success" size="small" />
            <Chip label="Encrypted Storage" color="success" size="small" />
            <Chip label="No External Sharing" color="success" size="small" />
            <Chip label="GDPR Compliant" color="success" size="small" />
          </Box>
        </CardContent>
      </Card>

      {/* AI Key / Trial Card */}
      <Card sx={styles.card}>
        <CardContent>
          <Typography sx={styles.cardTitle}>
            <Key />
            OpenAI API Key
          </Typography>

          {/* Trial status banner */}
          {aiKeyStatus?.mode === 'trial' && (
            <Alert
              severity="info"
              icon={<Warning />}
              sx={{ mb: 2 }}
            >
              <strong>Free trial: {aiKeyStatus.trialDaysRemaining} day{aiKeyStatus.trialDaysRemaining !== 1 ? 's' : ''} remaining</strong>
              {' '}— {aiKeyStatus.dailyRequestsRemaining} AI request{aiKeyStatus.dailyRequestsRemaining !== 1 ? 's' : ''} left today.
              Add your own key below to get unlimited access.
            </Alert>
          )}
          {aiKeyStatus?.mode === 'expired' && (
            <Alert severity="error" icon={<ErrorOutline />} sx={{ mb: 2 }}>
              <strong>Your free trial has expired.</strong> Add your OpenAI API key below to continue using AI features.
            </Alert>
          )}
          {aiKeyStatus?.mode === 'byok' && (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
              <strong>Your own OpenAI API key is active.</strong> AI features are using your key.
            </Alert>
          )}

          {/* API key input */}
          <TextField
            fullWidth
            type={showByokKey ? 'text' : 'password'}
            label="OpenAI API Key (sk-...)"
            placeholder="sk-..."
            value={byokKey}
            onChange={(e: any) => setByokKey(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Key sx={{ color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowByokKey(!showByokKey)} edge="end">
                    {showByokKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          {apiKeyError && <Alert severity="error" sx={{ mb: 2 }}>{apiKeyError}</Alert>}
          {apiKeySuccess && <Alert severity="success" sx={{ mb: 2 }}>{apiKeySuccess}</Alert>}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              disabled={!byokKey.trim() || savingApiKey}
              onClick={() => saveApiKey(byokKey)}
              sx={{ backgroundColor: '#e67e22', '&:hover': { backgroundColor: '#d35400' } }}
            >
              {savingApiKey ? 'Validating & Saving...' : 'Save API Key'}
            </Button>
            {aiKeyStatus?.hasByokKey && (
              <Button
                variant="outlined"
                color="error"
                disabled={savingApiKey}
                onClick={() => saveApiKey(null)}
              >
                Remove Key
              </Button>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Get your API key at{' '}
            <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">
              platform.openai.com/api-keys
            </a>. Your key is encrypted before storage and never shared.
          </Typography>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={saveAISettings}
          disabled={saving}
          sx={{
            backgroundColor: '#e67e22',
            '&:hover': {
              backgroundColor: '#d35400'
            }
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      {/* Folder Selection Dialog */}
      <Dialog 
        open={folderDialogOpen} 
        onClose={() => setFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          pb: 1
        }}>
          <FolderOpen sx={{ color: '#e67e22' }} />
          <Typography variant="h6" component="div" sx={{ flex: 1 }}>
            Choose Screenshot Folder
          </Typography>
          <IconButton 
            onClick={() => setFolderDialogOpen(false)}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Browse and select a folder where you want to save screenshots.
          </Typography>
          
          {/* Current Path Display */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            p: 2, 
            backgroundColor: '#f8f9fa', 
            borderRadius: 2, 
            mb: 2,
            border: '1px solid #e0e0e0'
          }}>
            <Computer sx={{ color: 'text.secondary', fontSize: '20px' }} />
            <Typography variant="body2" sx={{ 
              fontFamily: 'monospace', 
              fontSize: '13px',
              flex: 1,
              wordBreak: 'break-all'
            }}>
              {currentPath}
            </Typography>
            {currentPath !== '/Users' && (
              <IconButton 
                size="small" 
                onClick={navigateUp}
                sx={{ color: 'text.secondary' }}
              >
                <ArrowBack />
              </IconButton>
            )}
          </Box>

          {/* Folder Browser */}
          <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 2, 
            maxHeight: '300px', 
            overflow: 'auto',
            backgroundColor: 'white'
          }}>
            {loadingFolders ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {folders.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No folders found or unable to access this directory
                    </Typography>
                  </Box>
                ) : (
                  folders.map((folder, index) => (
                    <Button
                      key={index}
                      fullWidth
                      variant="text"
                      startIcon={<Folder sx={{ color: '#ffa726' }} />}
                      endIcon={<ChevronRight sx={{ color: 'text.secondary' }} />}
                      onClick={() => navigateToFolder(folder)}
                      sx={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        p: 2,
                        borderRadius: 0,
                        borderBottom: '1px solid #f0f0f0',
                        '&:hover': {
                          backgroundColor: '#f8f9fa'
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
                        {folder}
                      </Typography>
                    </Button>
                  ))
                )}
                
                {/* Create New Folder Button */}
                <Button
                  fullWidth
                  variant="text"
                  startIcon={<FolderOpen sx={{ color: '#e67e22' }} />}
                  onClick={async () => {
                    const folderName = prompt('Enter new folder name:');
                    if (folderName && folderName.trim()) {
                      const newPath = `${currentPath}/${folderName.trim()}`;
                      try {
                        await apiService.createFolder(newPath);
                        console.log('✅ Folder created successfully:', newPath);
                        setTempPath(newPath);
                        // Reload the current directory to show the new folder
                        loadFolders(currentPath);
                      } catch (error) {
                        console.error('❌ Failed to create folder:', error);
                        alert('Failed to create folder. Please check permissions and try again.');
                      }
                    }
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    p: 2,
                    borderRadius: 0,
                    borderTop: '1px solid #f0f0f0',
                    color: '#e67e22',
                    '&:hover': {
                      backgroundColor: '#fff3e0'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1, textAlign: 'left', fontWeight: '500' }}>
                    Create New Folder...
                  </Typography>
                </Button>
              </>
            )}
          </Box>

          {/* Selected Path Display */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="500" sx={{ mb: 1 }}>
              Selected Path:
            </Typography>
            <TextField
              fullWidth
              value={tempPath}
              onChange={(e) => setTempPath(e.target.value)}
              placeholder="Click on folders above or type path manually"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FolderOpen sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Click on folders above to navigate, or type the path manually. The folder will be created automatically if it doesn't exist.
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setFolderDialogOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              handleConfigChange('localSavePath', tempPath);
              setFolderDialogOpen(false);
            }}
            variant="contained"
            disabled={!tempPath.trim()}
            sx={{
              backgroundColor: '#e67e22',
              '&:hover': {
                backgroundColor: '#d35400'
              }
            }}
          >
            Save Path
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AISettings;
